/**
 * Module: api/notifications
 * Role: Typed wrappers over apiFetch for buyer_notifications endpoints (FTR-1034).
 *       Covers list, markRead, markAllRead, deleteNotification.
 *
 * Uses: ./client:apiFetch, @tanstack/react-query
 * Used by: screens/InboxScreen.tsx, screens/HomeScreen/index.tsx
 *
 * NOTE: TypeScript types below are a manual mirror of the Pydantic source-of-truth
 * at src/api/v2/buyer/_notification_schemas.py. Keep in sync when the Python models change.
 *
 * ADR-022 sibling-comment: `kind` Literal is symmetric with:
 *   - src/api/v2/buyer/_notification_schemas.py:NotificationItem.kind (Python Literal)
 *   - miniapp/src/screens/InboxScreen.tsx:cmap (color map keys)
 *   - supabase/migrations/20260518122000_ftr1034_buyer_notifications_table.sql (DB CHECK)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

// ---------------------------------------------------------------------------
// Types — manual mirror of src/api/v2/buyer/_notification_schemas.py
// ---------------------------------------------------------------------------

export type NotificationKind = 'success' | 'brand' | 'error' | 'warn' | 'muted';

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListMeta {
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface NotificationListView {
  items: NotificationItem[];
  meta: NotificationListMeta;
  unread_count: number;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const notifKeys = {
  list: () => ['buyer', 'notifications'] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Query: list buyer notifications, limit=30.
 * GET /api/v2/buyer/notifications?limit=30
 */
export function useNotifications() {
  return useQuery<NotificationListView>({
    queryKey: notifKeys.list(),
    queryFn: () => apiFetch<NotificationListView>('/api/v2/buyer/notifications?limit=30'),
  });
}

/**
 * Mutation: mark single notification as read.
 * POST /api/v2/buyer/notifications/{id}/read
 */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/v2/buyer/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.list() });
    },
  });
}

/**
 * Mutation: mark all notifications as read.
 * POST /api/v2/buyer/notifications/mark-all-read
 */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch<void>('/api/v2/buyer/notifications/mark-all-read', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.list() });
    },
  });
}

/**
 * Mutation: soft-delete a notification (swipe-to-delete).
 * DELETE /api/v2/buyer/notifications/{id}
 * Optimistic: removes from cache immediately, rolls back on error.
 */
export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/v2/buyer/notifications/${id}`, { method: 'DELETE' }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notifKeys.list() });
      const snapshot = qc.getQueryData<NotificationListView>(notifKeys.list());
      if (snapshot) {
        qc.setQueryData<NotificationListView>(notifKeys.list(), {
          ...snapshot,
          items: snapshot.items.filter((n) => n.id !== id),
        });
      }
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      const ctx = context as { snapshot?: NotificationListView } | undefined;
      if (ctx?.snapshot) {
        qc.setQueryData(notifKeys.list(), ctx.snapshot);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notifKeys.list() });
    },
  });
}
